import PropTypes from 'prop-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  List,
  Badge,
  Button,
  Avatar,
  Tooltip,
  Divider,
  Typography,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
  CircularProgress,
} from '@mui/material';
// utils
import { fToNow } from '../../../utils/formatTime';
import {
  POLL_INTERVAL_MS,
  fetchNotifications,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  notificationIcon,
  notificationLink,
} from '../../../utils/adminNotifications';
// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// components
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';

// ----------------------------------------------------------------------

const LATEST_LIMIT = 8;

export default function NotificationsPopover() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [totalUnRead, setTotalUnRead] = useState(0);
  const [loading, setLoading] = useState(false);

  // null until the first poll, so the badge filling up on page load isn't announced as "new".
  const lastUnread = useRef(null);

  const loadLatest = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchNotifications({ limit: LATEST_LIMIT });
      setNotifications(data.items || []);
      setTotalUnRead(data.unreadCount || 0);
      lastUnread.current = data.unreadCount || 0;
      return data.items || [];
    } catch (error) {
      console.error('notifications:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll the unread count; when it goes up, pull the list and announce the newest one.
  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      if (document.hidden) return;
      try {
        const count = await fetchUnreadCount();
        if (cancelled) return;
        const previous = lastUnread.current;
        setTotalUnRead(count);
        lastUnread.current = count;
        if (previous !== null && count > previous) {
          const items = await loadLatest();
          const newest = items.find((item) => !item.isRead);
          if (newest && !cancelled) enqueueSnackbar(newest.title, { variant: 'info' });
        }
      } catch (error) {
        // Logged-out / network blips: try again on the next tick.
      }
    };

    poll();
    const timer = setInterval(poll, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', poll);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', poll);
    };
  }, [enqueueSnackbar, loadLatest]);

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
    loadLatest();
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleMarkAllAsRead = async () => {
    setNotifications((items) => items.map((item) => ({ ...item, isRead: true })));
    setTotalUnRead(0);
    lastUnread.current = 0;
    try {
      await markAllNotificationsRead();
    } catch (error) {
      enqueueSnackbar('Could not mark notifications as read', { variant: 'error' });
      loadLatest();
    }
  };

  const handleClickItem = (notification) => {
    if (!notification.isRead) {
      setNotifications((items) => items.map((item) => (item._id === notification._id ? { ...item, isRead: true } : item)));
      setTotalUnRead((count) => Math.max(count - 1, 0));
      lastUnread.current = Math.max((lastUnread.current || 1) - 1, 0);
      markNotificationRead(notification._id).catch(() => {});
    }
    const link = notificationLink(notification);
    handleClose();
    if (link) navigate(link);
  };

  const handleViewAll = () => {
    handleClose();
    navigate(PATH_DASHBOARD.notifications.root);
  };

  return (
    <>
      <IconButtonAnimate color={open ? 'primary' : 'default'} onClick={handleOpen} sx={{ width: 40, height: 40 }}>
        <Badge badgeContent={totalUnRead} max={99} color="error">
          <Iconify icon="eva:bell-fill" width={20} height={20} />
        </Badge>
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{ width: 380, p: 0, mt: 1.5, ml: 0.75 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notifications</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {totalUnRead ? `You have ${totalUnRead} unread` : 'You are all caught up'}
            </Typography>
          </Box>

          {totalUnRead > 0 && (
            <Tooltip title="Mark all as read">
              <IconButtonAnimate color="primary" onClick={handleMarkAllAsRead}>
                <Iconify icon="eva:done-all-fill" width={20} height={20} />
              </IconButtonAnimate>
            </Tooltip>
          )}
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Scrollbar sx={{ maxHeight: { xs: 340, sm: 420 } }}>
          {loading && !notifications.length && (
            <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!loading && !notifications.length && (
            <Typography variant="body2" sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
              No notifications yet
            </Typography>
          )}

          <List disablePadding>
            {notifications.map((notification) => (
              <NotificationItem key={notification._id} notification={notification} onClick={handleClickItem} />
            ))}
          </List>
        </Scrollbar>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Box sx={{ p: 1 }}>
          <Button fullWidth disableRipple onClick={handleViewAll}>
            View All
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}

// ----------------------------------------------------------------------

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    _id: PropTypes.string,
    type: PropTypes.string,
    title: PropTypes.string,
    message: PropTypes.string,
    createdAt: PropTypes.string,
    isRead: PropTypes.bool,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

export function NotificationItem({ notification, onClick }) {
  const { icon, color } = notificationIcon(notification.type);

  return (
    <ListItemButton
      onClick={() => onClick(notification)}
      sx={{
        py: 1.5,
        px: 2.5,
        mt: '1px',
        ...(!notification.isRead && {
          bgcolor: 'action.selected',
        }),
      }}
    >
      <ListItemAvatar>
        <Avatar sx={{ bgcolor: 'background.neutral' }}>
          <Iconify icon={icon} width={22} height={22} sx={{ color }} />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography variant="subtitle2">
            {notification.title}
            {notification.message && (
              <Typography component="span" variant="body2" sx={{ color: 'text.secondary', display: 'block' }}>
                {notification.message}
              </Typography>
            )}
          </Typography>
        }
        secondary={
          <Typography
            variant="caption"
            sx={{
              mt: 0.5,
              display: 'flex',
              alignItems: 'center',
              color: 'text.disabled',
            }}
          >
            <Iconify icon="eva:clock-outline" sx={{ mr: 0.5, width: 16, height: 16 }} />
            {fToNow(notification.createdAt)}
          </Typography>
        }
      />
      {!notification.isRead && (
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', ml: 1, flexShrink: 0 }} />
      )}
    </ListItemButton>
  );
}
